import store from "../redux/store";
import axios from "axios";

// hooks and utilities
import { AircraftSize, getAircraftSizeCategory, getAircraftTypeGroup, getModelData } from "hooks/useModelData";

// classes
import Aircraft from "./aircraft";

// api
import api from "api/api";

// assets
import aircraftGroups from "assets/json/aircraftGroups.json";

/**
 * Enum for worker types.
 * @enum {WorkerType}
 */
class WorkerType {
	// TODO: Convert from creating an instance to using Symbols and .description
	static PILOT = new WorkerType("pilot");
	static FLIGHT_ATTENDANT = new WorkerType("flight_attendant");

	constructor(name) {
		this.name = name;
	}

	/**
	 * Create an enum from string
	 * @param {string} string
	 * @param {boolean} suppressErrors
	 * @return {WorkerType} WorkerType Enum
	 */
	static fromString(string, suppressErrors) {
		switch (string) {
			case "pilot":
				return WorkerType.PILOT;
			case "flight_attendant":
				return WorkerType.FLIGHT_ATTENDANT;
			default:
				if (!suppressErrors) throw new TypeError(`Unknown WorkerType '${string}'`);
		}
	}
}

/**
 * Abstract Staff class
 *
 * @class Staff
 * @abstract
 */
class Staff {
	/**
	 * A static method used to create a worker based on its type
	 * @param {Object} obj
	 * @return {Pilot | FlightAttendant | null} Returns the created worker or null if the worker type is unknown
	 * @static
	 */
	static createWorker(obj) {
		switch (obj.workerType) {
			case "pilot":
				return new Pilot(obj);
			case "flight_attendant":
				return new FlightAttendant(obj);
			default:
				console.error(`Unknown worker type '${obj.workerType}'`);
				return null;
		}
	}

	/**
	 * A static method used to get a worker based on an id
	 * @param {Number} id The identifier of the aircraft.
	 * @return {Array<Pilot | FlightAttendant> | undefined} Returns the worker or undefined if not found.
	 * @static
	 */
	static get(id) {
		return store.getState().staff.find(w => w.id === id);
	}

	/**
	 * Returns all workers assigned to the aircraft
	 * @param {number} aircraftId
	 * @returns {Array<Worker>}
	 * @static
	 */
	static getAssignedWorkers(aircraftId) {
		const staff = store.getState().staff;
		return staff.filter(w => w.aircraft === aircraftId);
	}

	/**
	 * @param {WorkerType} workerType
	 * @param {number} aircraftId
	 * @param {number|null} level Optional.
	 * @param {Array<Worker>|null} staff Optional.
	 * @returns {Worker|null}
	 * @static
	 */
	static getAssignedWorker(workerType, aircraftId, level, staff) {
		if (staff == null) {
			staff = store.getState().staff;
		}

		if (workerType === WorkerType.FLIGHT_ATTENDANT) {
			return staff.filter(w => w.workerType === workerType && w.aircraft === aircraftId && w.role === level);
		}

		return staff.find(
			w => w.workerType === workerType && w.aircraft === aircraftId && (level == null ? true : w.role === level)
		);
	}

	/**
	 * @param {WorkerType} workerType
	 * @param {number} aircraftId
	 * @returns {Worker|null}
	 * @static
	 */
	static getEligibleWorkers(workerType, aircraftId) {
		const staff = store.getState().staff;
		const hiredForAircraft = staff.filter(w => w.workerType === workerType && w.aircraft === aircraftId);
		if (workerType === WorkerType.FLIGHT_ATTENDANT) {
			var stewardessCount = hiredForAircraft.filter(w => w.role === 0).length;
			var requiredCount = Aircraft.get(aircraftId).requiredCabinCrew;
		}

		let busyRoles = hiredForAircraft.reduce(
			(acc, w) =>
				workerType === WorkerType.FLIGHT_ATTENDANT && w.role === 0 && stewardessCount < requiredCount
					? acc
					: [...acc, w.role],
			[]
		);

		let roles = [0, 1, 2, 3, 4, 5];
		if (workerType === WorkerType.PILOT) {
			const modelId = Aircraft.get(aircraftId).id;
			const aircraftData = getModelData(modelId);
			var [group] = getAircraftTypeGroup(modelId);
			roles = Array.from({ length: aircraftData.crew.pilots }, (_, i) => 5 - i);
		}

		const possibleLevels = findNumbersNotContained(roles, busyRoles);
		return staff.filter(w => {
			// eslint-disable-next-line eqeqeq
			if (workerType === WorkerType.PILOT && w.aircraftTypeGroup != group) return false;
			return w.workerType.name === workerType.name && w.aircraft == null && w.level >= Math.min(...possibleLevels);
		});
	}

	/**
	 * A static method used to get all pilots
	 * @return {Array<Pilot>} Returns an array of pilots.
	 * @static
	 */
	static getPilots() {
		return Pilot.getAll();
	}

	/**
	 * A static method used to get all flight attendants
	 * @param {Number} level Level of flight attendant to find
	 * @return {Array<FlightAttendant>} Returns an array of flight attendants.
	 * @static
	 */
	static getFlightAttendants(level) {
		return FlightAttendant.getAll(level).sort((a, b) => (b.aircraft != null) - (a.aircraft != null));
	}

	/**
	 * A static method used to get worker's base class
	 * @param {WorkerType} workerType
	 * @return {Pilot | FlightAttendant} Returns the base class (not instance)
	 * @static
	 */
	static getWorkerBaseClass(workerType) {
		switch (workerType) {
			case WorkerType.PILOT:
				return Pilot;
			case WorkerType.FLIGHT_ATTENDANT:
				return FlightAttendant;
			default:
				throw new TypeError(`Unknown worker type '${workerType}'`);
		}
	}

	/**
	 * @param {WorkerType} workerType
	 * @param {number} index
	 * @param {number} oldValue Old base salary.
	 * @param {number} newValue New base salary.
	 * @returns {Promise<void>}
	 * @async
	 * @static
	 */
	static async modifyBaseSalary(workerType, index, oldValue, newValue) {
		if (newValue <= 0 || newValue > 1000) {
			return;
		}

		store.dispatch({
			type: "CHANGE_BASE_STAFF_SALARY",
			payload: {
				workerType: workerType.name,
				index,
				value: newValue
			}
		});

		try {
			await axios.patch(`${api}/staff/${workerType.name}/changeSalary?index=${index}&value=${newValue}`);
		} catch (err) {
			console.error(err);
			console.error(err.response?.data);
			revert();
			alert("Wystąpił jakiś błąd! \n Some error has occurred!");
		}

		function revert() {
			store.dispatch({
				type: "CHANGE_BASE_STAFF_SALARY",
				payload: {
					workerType: workerType.name,
					index,
					value: oldValue
				}
			});
		}
	}
}

/**
 * Abstract Worker class
 *
 * @class Worker
 * @abstract
 */
class Worker {
	role;
	level;
	experience;
	salaryModifier;

	#id;
	#name;
	#workerType;
	#aircraft;
	#hiredSince;

	constructor({ id, name, workerType, experience, level, assignedLevel, aircraftId, salaryModifier, hiredSince }) {
		if (this.constructor === Worker) {
			throw new Error("Abstract class 'Worker' can't be instantiated");
		}

		this.#id = +id;
		this.#name = name;
		this.#workerType = WorkerType.fromString(workerType);
		this.experience = +experience;
		this.level = +level;
		this.role = +assignedLevel;
		this.#aircraft = aircraftId;
		this.salaryModifier = +salaryModifier;
		this.#hiredSince = hiredSince;
		return this;
	}

	// init() {
	// 	if (this.aircraft == null || this.#initialised) return this;
	// 	this.#initialised = true;
	// 	this.role = getRole.call(this);

	// 	function getRole() {
	// 		if (this.aircraft == null) return null;

	// 		const allStaff = store.getState().staff;

	// 		// All staff of same type assigned to the same aircraft without 'this' worker.
	// 		const forAircraft = allStaff.filter(
	// 			w => w.workerType === this.workerType && w.aircraft === this.aircraft && w.id !== this.id
	// 		);

	// 		const usedRoles = forAircraft.map(w => w.role).filter(r => r);
	// 		const modelId = Aircraft.get(this.aircraft).id;
	// 		const { pilots: requiredPilotsCount, cabin_crew: requiredCrewCount } = getModelData(modelId).crew;
	// 		let pilotLevels = [];

	// 		for (let i = 5; i > 5 - requiredPilotsCount; i--) {
	// 			pilotLevels.push(i);
	// 		}

	// 		const requirements = new Map([
	// 			[WorkerType.PILOT, pilotLevels],
	// 			[WorkerType.FLIGHT_ATTENDANT, requiredCrewCount > 0 ? [1, ...Array(requiredCrewCount - 1).fill(0)] : []]
	// 		]);

	// 		const missing = removeElementsFromArray(requirements.get(this.workerType), usedRoles);

	// 		for (let l = this.level; l >= 0; l--) {
	// 			if (missing.includes(l)) {
	// 				return l;
	// 			}
	// 		}

	// 		throw new Error("Something went wrong");
	// 	}

	// 	return this;
	// }

	get id() {
		return this.#id;
	}

	get name() {
		return this.#name;
	}

	/**
	 * @returns {number | null}
	 */
	get aircraft() {
		return this.#aircraft;
	}

	/**
	 * @param {number | null} newAircraft
	 */
	set aircraft(newAircraft) {
		(async () => {
			try {
				const response = await axios.patch(`${api}/staff/worker/${this.id}`, { aircraft: newAircraft });
				if (isNaN(+response.data)) {
					console.error(response.data);
					throw new TypeError("Data returned from PHP is invalid. Check for PHP errors above");
				}

				this.role = newAircraft === null ? null : +response.data;
				this.#aircraft = newAircraft;
				store.dispatch({ type: "UPDATE_STAFF" });
			} catch (err) {
				if (err?.response?.status === 409) {
					alert("Nie można zmienić składu załogi! \nCannot change the crew!");
					return;
				}

				console.error(err);
				err.response && console.error(err.response?.data);
				alert("Wystąpił jakiś błąd! \nSome error has occurred!");
			}
		})();
	}

	/**
	 * @return {WorkerType} worker type
	 */
	get workerType() {
		return this.#workerType;
	}

	get hiredSince() {
		return this.#hiredSince;
	}

	get workerPosition() {
		throw new Error("Getter 'workerPosition' must be implemented");
	}

	get salary() {
		if (isNaN(this.salaryModifier)) {
			throw new TypeError(
				`Expected 'salaryModifier' to be of type 'number' but encountered '${typeof this.salaryModifier}'`
			);
		}

		return Math.round(this.salaryModifier * this.getBaseSalary());
	}

	get roleText() {
		return this.role ? Staff.getWorkerBaseClass(this.workerType).workerPosition(this.role) : "ładowanie";
	}

	/**
	 * @param {boolean} f Optional. If false won't throw an error if this.aircraft == null
	 * @param {number} aircraftToAssign Required if f == false.
	 * @returns {number}
	 */
	getBaseSalary(f = true, aircraftToAssign, role) {
		if (f && this.aircraft == null) {
			return 0;
		}

		let index;
		if (this.workerType === WorkerType.FLIGHT_ATTENDANT) {
			index = this.role || role;
		} else {
			const seatCount = getModelData(Aircraft.get(this.aircraft || aircraftToAssign).id).passengers;
			index = +getAircraftSizeCategory(seatCount).description;
		}

		return store.getState().airlineInfo.staffSalary[this.workerType.name][index];
	}

	/**
	 * Removes the worker from the database and redux
	 * @returns {Promise<boolean>}
	 */
	async fire() {
		if (this.aircraft != null) {
			const a = Aircraft.get(this.aircraft);
			if (a.status === "inflight") {
				alert(
					"Nie można zwolnić pracownika, jeśli znajduje się on w samolocie, który jest w trakcie lotu! \n Cannot fire an employee that is onboard an active flight!"
				);
				return false;
			}

			const hub = store.getState().airlineInfo.hubs[0];
			if (a.location !== hub) {
				alert(
					"Nie można zwolnić pracownika, jeśli nie znajduje się on w bazie! \n Cannot fire an employee that isn't in the hub!"
				);
				return false;
			}

			const confirmation = window.confirm(
				`Czy na pewno chcesz zwolnić ${
					this.#name
				}? Ten pracownik jest przypisany do samolotu!\n\nAre you sure you want to fire ${
					this.#name
				}? This employee is assigned to an aircraft!`
			);

			if (!confirmation) return false;
		}

		try {
			await axios.delete(`${api}/staff/worker/${this.id}`);
			store.dispatch({ type: "DELETE_WORKER", payload: this.id });
			return true;
		} catch (err) {
			console.error(err);
			console.error(err?.cause);
			return false;
		}
	}

	/**
	 * Always returns a salary > 0
	 * @param {number} aircraftId An id of an aircraft that is to be assigned.
	 * @returns {number} Salary. Always > 0.
	 * */
	getSalary(aircraftId) {
		if (isNaN(this.salaryModifier)) {
			throw new TypeError(
				`Expected 'salaryModifier' to be of type 'number' but encountered '${typeof this.salaryModifier}'`
			);
		}

		let role = null;
		if (this.workerType === WorkerType.FLIGHT_ATTENDANT) {
			if (this.level === 0) {
				role = 0;
			} else {
				const purserAssigned = Staff.getAssignedWorkers(aircraftId).filter(
					w => w instanceof FlightAttendant && w.level === 1
				).length;
				role = purserAssigned ? 0 : 1;
			}
		}

		return Math.round(this.salaryModifier * this.getBaseSalary(false, aircraftId, role));
	}

	/** Sets the aircraft to the provided value.
	 * Does not preform any checks.
	 * Does not save in the database nor updates the redux.
	 * @param {number|null} newAircraft
	 */
	setAircraft(newAircraft) {
		this.#aircraft = newAircraft;
	}
}

/**
 * @class Pilot
 * @extends Worker
 */
class Pilot extends Worker {
	constructor(obj) {
		super(obj);

		this.aircraftTypeGroup = obj.aircraftTypeGroup;
		return this;
	}

	get workerPosition() {
		return Pilot.workerPosition(this.level);
	}

	static workerPosition(level) {
		switch (level) {
			case 0:
				return ["Flight technician", "Technik pokładowy", "fas fa-toolbox"];
			case 1:
				return ["Radio operator", "Radiooperator", "fas fa-radio"];
			case 2:
				return ["Navigator", "Nawigator", "fas fa-map-location-dot"];
			case 3:
				return ["Flight engineer", "Mechanik pokładowy", "fas fa-wrench"];
			case 4:
				return ["First officer", "Pierwszy oficer", "fas fa-splotch"];
			case 5:
				return ["Captain", "Kapitan", "fas fa-award"];
			default:
				throw new TypeError(`Expected 'level' to be in range 0 to 5 but got ${level}`);
		}
	}

	/**
	 * @param {string} Id of the aircraft group or the aircraft model
	 * @return {string} The name of  the aircraft (or aircraft group)
	 * @static
	 */
	static getAircraftType(aircraftTypeGroup) {
		if (typeof aircraftTypeGroup != "string" && typeof aircraftTypeGroup != "number") {
			throw new TypeError(
				`Parameter 'aircraftTypeGroup' must be of type 'string' but got '${typeof aircraftTypeGroup}'`
			);
		}

		if (isNaN(+aircraftTypeGroup)) {
			const modelData = getModelData(aircraftTypeGroup);
			return modelData.name;
		}

		const group = aircraftGroups.modelGroups.find(gr => gr.id === +aircraftTypeGroup);
		return group.name;
	}

	/**
	 * A static method used to get all pilots
	 * @return {Array<Pilot>} Returns an array of pilots.
	 * @static
	 */
	static getAll() {
		return store.getState().staff.filter(w => w.workerType === WorkerType.PILOT);
	}

	/**
	 * A static method used to get all pilots but grouped by aircraft types.
	 * @return {Map<string, Array<Pilot>>} Returns an array of arrays of pilots (grouped by aircraft type). Key is in the format: "name;ID"
	 * @static
	 */
	static getGroupedByAircraftType() {
		const allPilots = Pilot.getAll();
		const grouped = allPilots.reduce((acc, p) => {
			let key;

			if (p.aircraft == null) {
				const lang = store.getState().lang;
				key = `${["Not assigned", "Nie przypisani"][+(lang === "PL")]};none`;
			} else {
				// TODO: Optimize (cache)
				const aircraft = Aircraft.get(p.aircraft);
				const modelName = getModelData(aircraft.id).name;
				key = `${modelName};${aircraft.id}`;
			}

			if (acc.has(key)) {
				acc.get(key).push(p);
			} else {
				acc.set(key, [p]);
			}

			return acc;
		}, new Map());

		const sorted = new Map(
			[...grouped].sort(([aKey, a], [bKey, b]) => {
				// if (!a.aircraft && !b.aircraft) return 0;
				// if (a.aircraft && !b.aircraft) return 1;
				// if (a.aircraft && b.aircraft) return -1;

				let aModelId = aKey.split(";")[1];
				let bModelId = bKey.split(";")[1];

				if (aModelId === "none") {
					return 1;
				}

				if (bModelId === "none") {
					return -1;
				}

				if (aModelId === "none" && bModelId === "none") {
					return 0;
				}

				if (!isNaN(+aModelId)) {
					const group = aircraftGroups.modelGroups.find(gr => gr.id === +aModelId);
					aModelId = group.models[0];
				}

				if (!isNaN(+bModelId)) {
					const group = aircraftGroups.modelGroups.find(gr => gr.id === +bModelId);
					bModelId = group.models[0];
				}

				const modelA = getModelData(aModelId);
				const modelB = getModelData(bModelId);
				const sizeA = getAircraftSizeCategory(modelA.passengers);
				const sizeB = getAircraftSizeCategory(modelB.passengers);

				return +sizeA.description > +sizeB.description ? -1 : 1;
			})
		);

		return sorted;
	}

	/**
	 * A static method used to get all pilots but grouped by aircraft types.
	 * @return {Map<string, Array<Pilot>>} Returns an array of arrays of pilots (grouped by aircraft type). Key is in the format: "name;ID"
	 * @static
	 */
	static getGroupedByAircraftSize() {
		const allPilots = Pilot.getAll();
		const idMap = new Map();
		const modelMap = new Map();

		const grouped = allPilots.reduce((acc, p) => {
			let cat;

			if (p.aircraft == null) {
				cat = AircraftSize.NONE;
			} else if (idMap.has(p.aircraft)) {
				cat = getAircraftSizeCategory(idMap.get(p.aircraft));
			} else {
				const modelId = Aircraft.get(p.aircraft).id;

				if (modelMap.has(modelId)) {
					cat = getAircraftSizeCategory(modelMap.get(modelId));
				} else {
					const seatCount = getModelData(modelId).passengers;
					cat = getAircraftSizeCategory(seatCount);

					modelMap.set(modelId, seatCount);
					idMap.set(p.aircraft, seatCount);
				}
			}

			if (acc.has(cat)) {
				acc.get(cat).push(p);
			} else {
				acc.set(cat, [p]);
			}

			return acc;
		}, new Map());

		return grouped;
	}

	/**
	 * @return {number} Number of currently hired pilots
	 * @static
	 */
	static get amount() {
		return Pilot.getAll().length;
	}

	/**
	 * @return {number} Total pilots pay
	 */
	static get totalSalary() {
		return getTotalSalaryForWorkers(Pilot.getAll());
	}
}

/**
 * @class Pilot
 * @extends Worker
 */
class FlightAttendant extends Worker {
	constructor(obj) {
		super(obj);
		return this;
	}

	/**
	 * @return {Array<String>}
	 */
	get workerPosition() {
		return FlightAttendant.workerPosition(this.level);
	}

	get salary() {
		if (isNaN(this.salaryModifier)) {
			throw new TypeError(
				`Expected 'salaryModifier' to be of type 'number' but encountered '${typeof this.salaryModifier}'`
			);
		}

		return Math.round(this.salaryModifier * store.getState().airlineInfo.staffSalary[this.workerType.name][this.role]);
	}

	/**
	 * A static method used to get all flight attendants
	 * @param {Number} level Level of flight attendant to find
	 * @return {Array<FlightAttendant>} Returns an array of flight attendants.
	 * @static
	 */
	static getAll(level) {
		const all = store.getState().staff;
		const filtered = all.filter(
			w => w.workerType === WorkerType.FLIGHT_ATTENDANT && (level != null ? w.level === level : true)
		);

		filtered.sort((a, b) => (a.aircraft == null ? 1 : b.experience - a.experience));

		return filtered;
	}

	/**
	 * @return {number} Number of currently hired pilots
	 * @static
	 */
	static get amount() {
		return FlightAttendant.getAll().length;
	}

	/**
	 * @return {number} Total flight attendants pay
	 */
	static get totalSalary() {
		return getTotalSalaryForWorkers(FlightAttendant.getAll());
	}

	/**
	 * @param {number} level Number indicating employee level. Range: 0 - 1.
	 * @return {Array<String>}
	 */
	static workerPosition(level) {
		switch (level) {
			case 0:
				return ["Stewardess", "Stewardessa"];
			case 1:
				return ["Purser", "Szef pokładu"];
			default:
				throw new TypeError(`Expected 'FlightAttendant.level' to be in range 0 to 1 but got ${this.level}`);
		}
	}
}

/**
 * @param {Array<Worker>} workers Array of workers to sum salary
 * @return {number} Total salary of any workers provided
 */
function getTotalSalaryForWorkers(workers) {
	return workers.reduce((acc, worker) => (acc += worker.salary), 0);
}

export { Staff, Worker, WorkerType, Pilot, FlightAttendant };

function findNumbersNotContained(arr1, arr2) {
	const result = [];
	const setArr2 = new Set(arr2);

	for (const num of arr1) {
		if (!setArr2.has(num)) result.push(num);
	}

	return result;
}
